import {
  currentBrand,
  isHmBrand,
  getCookie,
  getTouchpoint,
  isNorthstarBrand,
} from '@pb/utils'

import appSelectors from 'App/duck/selectors'
import selectors from 'features/cart/cart-overview/duck/selectors'
import { CART_TRACKING_EVENTS } from 'features/cart/constants'
import voucherSelectors from 'features/vouchers/duck/selectors'
import toUppercasedBooleanString from 'helpers/toUppercasedBooleanString'
import { buildCustomTrackers } from 'helpers/tracking'
import { ThunkResult } from 'redux/types'

import { TrackingTypes } from './types'

export const getABTestData = (state): any => ({
  hazmatItems: selectors.getHazmatItems(state),
  oosItems: selectors.getOOSItems(state),
  items: selectors.getCartItems(state),
  userId: appSelectors.getUserId(state),
})

const getGeneralTracking = (): object => ({ category_path: 'Checkout/Shopping Bag' })

const getGeneralTrackingSingleItem = (item): object => {
  return {
    product_brand: [
      item.brand === 'SELLPY'
        ? 'SELLPY'
        : item.brandsData.brandName || (isHmBrand && 'H&M') || currentBrand,
    ],
    product_category: [item.categoryCode?.toUpperCase() || ''],
    product_id: [item.baseProductCode?.substring(0, 7) || ''],
    product_name: [item.internalName?.toUpperCase() || ''],
    product_original_price: [item.whitePrice?.formattedValue || ''],
    product_quantity: [item.quantity?.toString() || ''],
    product_size_code: [item.sizeCode || ''],
    product_marker_marketing:
      item.cartProductMarker?.text || (item.condition && `Condition_${item.condition}`) || 'FALSE',
    product_marker_selection: item.sellingAttribute || 'FALSE',
  }
}

const getGeneralTrackingMultipleItems = (items, state): object => {
  return {
    product_brand: items.map((item) =>
      item.brand === 'SELLPY'
        ? 'SELLPY'
        : item.brandsData.brandName || (isHmBrand && 'H&M') || currentBrand,
    ),
    product_category: items.map((item) => item.categoryCode?.toUpperCase() || ''),
    product_id: items.map((item) => item.baseProductCode?.substring(0, 7) || ''),
    product_name: items.map((item) => item.internalName?.toUpperCase() || ''),
    product_original_price: items.map((item) => item.whitePrice?.formattedValue || ''),
    product_quantity: items.map((item) => item.quantity?.toString() || ''),
    product_size_code: items.map((item) => item.sizeCode || ''),
    product_marker_marketing: items.map(
      ({ cartProductMarker, condition }) =>
        cartProductMarker?.text || (condition && `Condition_${condition}`) || 'FALSE',
    ),
    product_marker_selection: items.map(({ sellingAttribute }) => sellingAttribute || 'FALSE'),
    // If there only is hm products in the cart, length of brands will be equal to 0, thereby the second check
    cart_parcel:
      selectors.getBrands(state).length || (selectors.getCartItems(state).length > 0 ? 1 : 0),
    dep_variable_format: ['integer'],
    dep_variable_name: ['cart_parcel'],
  }
}

const getHmSpecificTracking = (state, items): object => {
  return {
    customer_id: appSelectors.getUserId(state) || '',
    sb_empty: (items.length === 0).toString(),
    sb_id: selectors.getCartCode(state) || '',
  }
}

const getHmSpecificTrackingSingleItem = (state, item): object => {
  const productSize = (item.variants || [])
    .filter((variant) => variant.code === item.baseProductCode)
    .map((variant) => variant.size.name)
  productSize.push(item.size)

  const productSizeRec = [
    sessionStorage.getItem(`recsize-${item.baseProductCode?.substring(0, 7) || ''}`) ||
      'No recommendation available',
  ]

  return {
    product_cart_starter: [item.isCartStarter?.toString() || 'FALSE'],
    product_few_pieces: [item.fewPieceLeft?.toString() || 'FALSE'],
    product_color_code: [item.colorCode || ''],
    product_list_price: [item.productPrice?.value?.toString() || ''],
    product_currency: [appSelectors.getCurrentCurrency(state)?.isocode || ''],
    product_price_type: [item.priceType],
    product_osa_area: [item.onSiteAttributeMap?.product_osa_area || ''],
    product_osa_type: [item.onSiteAttributeMap?.product_osa_type || ''],
    product_article_id: [item.productCode || ''],
    product_video: [toUppercasedBooleanString(item.onSiteAttributeMap?.productVideo || '')],
    product_size: productSize,
    product_rating: [''],
    product_rating_avg: [''],
    product_size_rec: productSizeRec,
    imp_origin_system: [item.onSiteAttributeMap?.product_pra_origin || ''],
  }
}

const getHmSpecificTrackingMultipleItems = (state, items): object => {
  return {
    order_giftcard: '',
    order_subtotal: selectors.getCartSubTotal(state).toFixed(2) || '',
    product_cart_starter: items.map((item) => item.isCartStarter?.toString() || 'FALSE'),
    product_few_pieces: items.map((item) => item.fewPieceLeft?.toString() || 'FALSE'),
    product_color_code: items.map((item) => item.colorCode || ''),
    product_list_price: items.map((item) => item.productPrice?.value?.toString() || ''),
    product_currency: appSelectors.getCurrentCurrency(state)?.isocode || '',
    product_price_type: items.map((item) => item.priceType || ''),
    product_osa_area: items.map((item) => item.onSiteAttributeMap?.product_osa_area || ''),
    product_osa_type: items.map((item) => item.onSiteAttributeMap?.product_osa_type || ''),
    product_article_id: items.map((item) => item.productCode || ''),
    product_video: items.map((item) =>
      toUppercasedBooleanString(item.onSiteAttributeMap?.productVideo || ''),
    ),
    imp_origin_system: items.map((item) => item.onSiteAttributeMap?.product_pra_origin || ''),
  }
}

const getBrandSpecificTracking = (state): object => {
  const cookieLocaleSplit = getCookie('HMCORP_locale')?.split(/[-_]/)
  const regionMarket = cookieLocaleSplit ? (cookieLocaleSplit.pop() as string) : ''

  return {
    page_id: 'CART',
    product_view_type: 'cart',
    checkout_steps: 1,
    list_action: '',
    region_currency: getCookie('HMCORP_currency') || '',
    region_locale: getCookie('HMCORP_locale') || '',
    region_market: regionMarket || '',
    session_touchpoint: getTouchpoint() || '',
    product_coupon:
      voucherSelectors.getAppliedDiscounts(state).length > 0
        ? voucherSelectors
            .getAppliedDiscounts(state)
            .map((discount) => (discount?.voucherCode ? discount.voucherCode : ''))
        : [''],
  }
}

const getBrandSpecificTrackingSingleItem = (item): object => {
  return {
    product_atelier: [''],
    product_color: [item.color || ''],
    product_origin: [''],
    product_price: [item.productPrice?.value?.toString() || ''],
    product_size: [item.size || ''],
  }
}

const getBrandSpecificTrackingMultipleItems = (items): object => {
  return {
    product_atelier: items.map(() => ''),
    product_color: items.map((item) => item.colorCode || ''),
    product_name_id: items.map((item) => `${item.productName} | ${item.productCode}`),
    product_origin: items.map(() => ''),
    product_price: items.map((item) => item.productPrice?.value?.toString() || ''),
    product_sales_percentage: [''],
    product_size: items.map((item) => item.size || ''),
    product_variant: items.map((item) => item.variantCode || ''),
  }
}

export const getShoppingBagTrackingInfo = (state): any => {
  const items = selectors.getCartItems(state)

  const generaltracking = getGeneralTracking()
  const generalTrackingMultipleItems = getGeneralTrackingMultipleItems(items, state)

  if (!(isHmBrand || isNorthstarBrand)) {
    const brandSpecificTracking = getBrandSpecificTracking(state)
    const brandSpecificTrackingMultipleItems = getBrandSpecificTrackingMultipleItems(items)

    return {
      ...generaltracking,
      ...generalTrackingMultipleItems,
      ...brandSpecificTracking,
      ...brandSpecificTrackingMultipleItems,
    }
  }

  const hmSpecificTracking = getHmSpecificTracking(state, items)
  const hmSpecificTrackingMultipleItems = getHmSpecificTrackingMultipleItems(state, items)

  return {
    ...generaltracking,
    ...generalTrackingMultipleItems,
    ...hmSpecificTracking,
    ...hmSpecificTrackingMultipleItems,
    customer_username: appSelectors.getUserEmail(state) || '',
  }
}

export const getCartItemTrackingInfo = (variantCode, state): any => {
  const items = selectors.getCartItems(state)
  const item = selectors.getCartItem(variantCode, state)

  const generaltracking = getGeneralTracking()
  const generalTrackingSingleItem = getGeneralTrackingSingleItem(item)

  if (!(isHmBrand || isNorthstarBrand)) {
    const brandSpecificTracking = getBrandSpecificTracking(state)
    const brandSpecificTrackingSingleItem = getBrandSpecificTrackingSingleItem(item)

    return {
      ...generaltracking,
      ...generalTrackingSingleItem,
      ...brandSpecificTracking,
      ...brandSpecificTrackingSingleItem,
    }
  }

  const hmSpecificTracking = getHmSpecificTracking(state, items)
  const hmSpecificTrackingSingleItem = getHmSpecificTrackingSingleItem(state, item)

  return {
    ...generaltracking,
    ...generalTrackingSingleItem,
    ...hmSpecificTracking,
    ...hmSpecificTrackingSingleItem,
  }
}

const shoppingBagCsTracking = (): any =>
  buildCustomTrackers({
    staticDataMap: CART_TRACKING_EVENTS,
    persistentData: getHmSpecificTracking,
  })

export const getGiftCardTrackingInfo = (cardNumber, state): TrackingTypes['giftCardTracking'] => {
  const giftCards = selectors.getAppliedGiftCards(state)
  const giftCard = giftCards.find((item) => item.cardNumber === cardNumber)
  return (
    giftCard && {
      giftcard_balance_amount: giftCard?.balanceAmount.value.toString(),
      giftcard_remain_amount: giftCard?.remainAmount.value.toString(),
      giftcard_eov_date: giftCard?.eovDate,
      giftcard_currency: giftCard?.currency,
      giftcard_deducted_amount: giftCard?.deductedAmount.value.toString(),
    }
  )
}

export const getGiftCardErrorMessage = (state): TrackingTypes['giftCardErrorMessage'] => {
  const giftCardErrorMessage = selectors.getGiftCardError(state).message
  return giftCardErrorMessage
}

export const trackViewEvent =
  ({ event, clickTale }): ThunkResult<void> =>
  () =>
    shoppingBagCsTracking().trackViewEvent(event, clickTale)()
